import type {
  CompanyGeneralInfo,
  UpdateCompanyGeneralInfoRequest
} from '@tandemone/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useReducer } from 'react'

import { extractErrorMessage } from '@/api/extract-error'
import { useDirtyVsInitial } from '@/domains/companies'
import { isValidWebsite, normalizeWebsite } from '@/lib/url'

import { updateCompanyGeneralInfo } from './api'
import {
  type GeneralInfoDerived,
  type GeneralInfoFormAction,
  type GeneralInfoFormState,
  GeneralInfoFormContext
} from './general-info-context'
import { invalidateOnboarding } from './invalidate-onboarding'

function reducer(
  state: GeneralInfoFormState,
  action: GeneralInfoFormAction
): GeneralInfoFormState {
  switch (action.type) {
    case 'setString':
      return { ...state, [action.field]: action.value }
    case 'setBool':
      return { ...state, [action.field]: action.value }
    case 'setLogoId':
      return { ...state, logoId: action.value }
    case 'touch':
      return { ...state, [`${action.field}Touched`]: true }
    case 'addressPicked':
      return {
        ...state,
        countryCode: action.countryCode,
        country: action.country,
        region: action.region,
        latitude: action.latitude,
        longitude: action.longitude
      }
    case 'addressInvalidated':
      return {
        ...state,
        countryCode: null,
        country: '',
        region: '',
        latitude: null,
        longitude: null
      }
    case 'submitError':
      return { ...state, submitError: action.message }
    default:
      return state
  }
}

function initialState(generalInfo: CompanyGeneralInfo): GeneralInfoFormState {
  return {
    companyName: generalInfo.name,
    website: generalInfo.website ?? '',
    location: generalInfo.location ?? '',
    yearFounded: generalInfo.yearFounded?.toString() ?? '',
    dunsNumber: generalInfo.dunsNumber ?? '',
    parentCompanyName: generalInfo.parentCompanyName ?? '',
    description: generalInfo.description ?? '',
    logoId: generalInfo.logoId,
    isPublic: generalInfo.isPublic,
    isSubsidiary: generalInfo.isSubsidiary,
    countryCode: null,
    country: generalInfo.country ?? '',
    region: generalInfo.region ?? '',
    latitude: generalInfo.latitude,
    longitude: generalInfo.longitude,
    websiteTouched: false,
    yearFoundedTouched: false,
    dunsNumberTouched: false,
    submitError: null
  }
}

function derive(
  state: GeneralInfoFormState,
  initialLocation: string
): GeneralInfoDerived {
  const yearFoundedInt =
    state.yearFounded === '' ? null : Number.parseInt(state.yearFounded, 10)
  const currentYear = new Date().getFullYear()
  const yearFoundedInvalid =
    state.yearFounded !== '' &&
    (state.yearFounded.length !== 4 ||
      Number.isNaN(yearFoundedInt) ||
      yearFoundedInt! < 1700 ||
      yearFoundedInt! > currentYear)
  const dunsNumberInvalid =
    state.dunsNumber !== '' && state.dunsNumber.length !== 9
  const websiteInvalid =
    state.website.trim() !== '' && !isValidWebsite(state.website)
  // an address typed but never picked from the suggestions has no country, so
  // the server rejects it. scoped to a changed address to match that gate, else
  // a legacy row saved without a country can never be edited again.
  const locationUnresolved =
    state.location.trim() !== '' &&
    state.location.trim() !== initialLocation.trim() &&
    state.country.trim() === ''
  const isValid =
    state.companyName.trim() !== '' &&
    state.website.trim() !== '' &&
    !websiteInvalid &&
    state.location.trim() !== '' &&
    !locationUnresolved &&
    state.yearFounded.trim() !== '' &&
    !yearFoundedInvalid &&
    !dunsNumberInvalid &&
    (!state.isSubsidiary || state.parentCompanyName.trim() !== '')

  return {
    yearFoundedInt,
    yearFoundedInvalid,
    dunsNumberInvalid,
    websiteInvalid,
    locationUnresolved,
    isValid,
    currentYear
  }
}

export function GeneralInfoFormProvider({
  children,
  generalInfo,
  onSuccess
}: {
  children: ReactNode
  generalInfo: CompanyGeneralInfo
  onSuccess: () => void
}) {
  const queryClient = useQueryClient()
  const [state, dispatch] = useReducer(reducer, initialState(generalInfo))
  /**
   * register with the dirty-form registry so the company switcher
   * confirm-dialog gate fires on a half-edited general-info step.
   * ignore *Touched (blur-tracking, not user content) and submitError
   * (server-side feedback, not user input).
   */
  useDirtyVsInitial('general-info-form', state, [
    'websiteTouched',
    'yearFoundedTouched',
    'dunsNumberTouched',
    'submitError'
  ])

  const mutation = useMutation({
    mutationFn: (body: UpdateCompanyGeneralInfoRequest) =>
      updateCompanyGeneralInfo(body),
    onSuccess: async () => {
      await invalidateOnboarding(queryClient, 'company-general-info')
      onSuccess()
    },
    onError: failure =>
      dispatch({ type: 'submitError', message: extractErrorMessage(failure) })
  })

  const derived = derive(state, generalInfo.location ?? '')

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!derived.isValid) return
    dispatch({ type: 'submitError', message: null })
    mutation.mutate({
      companyName: state.companyName.trim(),
      website: normalizeWebsite(state.website),
      location: state.location.trim(),
      yearFounded: derived.yearFoundedInt,
      dunsNumber:
        state.dunsNumber.trim() === '' ? null : state.dunsNumber.trim(),
      countryCode: state.countryCode,
      country: state.country.trim() === '' ? null : state.country.trim(),
      region: state.region.trim() === '' ? null : state.region.trim(),
      latitude: state.latitude,
      longitude: state.longitude,
      isPublic: state.isPublic,
      isSubsidiary: state.isSubsidiary,
      parentCompanyName: state.isSubsidiary
        ? state.parentCompanyName.trim()
        : null,
      description:
        state.description.trim() === '' ? null : state.description.trim(),
      logoId: state.logoId
    })
  }

  return (
    <GeneralInfoFormContext.Provider
      value={{
        state,
        derived,
        dispatch,
        isSubmitting: mutation.isPending,
        handleSubmit
      }}
    >
      {children}
    </GeneralInfoFormContext.Provider>
  )
}
